import React, { useEffect, useRef, useState } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Box,
  Text,
  RadioGroup,
  Radio,
  TextArea,
  useToast,
} from '@razorpay/blade/components';
import { useMutation, useQueryClient } from 'react-query';
import { useHistory } from 'react-router-dom';
import api from 'api';
import employeeBankAccountApis from 'api/employeeBankAccount';
import { MISSING_REPORT_LIST, MISSING_REPORT_SUMMARY } from 'api/missingInformationReport';
import { AppError } from 'utils/AppError';
import { parseError } from 'utils/Api';
import { routePaths } from 'components/Routes/data';
import { MODAL_OVER_DRAWER_Z_INDEX } from '../data';
import { NameMismatchRejectModalProps, RejectAction } from '../types';

export const NameMismatchRejectModal = ({
  isOpen,
  onClose,
  onRejectSuccess,
  selectedAccount,
}: NameMismatchRejectModalProps) => {
  const [selectedAction, setSelectedAction] = useState<RejectAction>('send_reminder');
  const [reason, setReason] = useState('');
  const pendingRejectActionRef = useRef<RejectAction>('send_reminder');
  const toast = useToast();
  const history = useHistory();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!isOpen) {
      setSelectedAction('send_reminder');
      setReason('');
    }
  }, [isOpen]);

  const invalidateReportQueries = () => {
    queryClient.invalidateQueries({ queryKey: [MISSING_REPORT_SUMMARY] });
    queryClient.invalidateQueries({ queryKey: [MISSING_REPORT_LIST] });
  };

  const { mutate: sendReminder } = useMutation({
    mutationFn: api.missingInformationReport.sendReminder,
    onSuccess: () => {
      toast.show({
        content: 'Reminder sent successfully',
        color: 'positive',
      });
    },
    onError: (error: typeof AppError) => {
      toast.show({
        content: parseError(error),
        color: 'negative',
      });
    },
  });

  const { mutate: rejectNameMismatch, isLoading: isRejecting } = useMutation({
    mutationFn: employeeBankAccountApis.rejectNameMismatch,
    onSuccess: () => {
      invalidateReportQueries();

      toast.show({
        content: 'Bank account rejected successfully, sending reminder now',
        color: 'positive',
      });

      if (pendingRejectActionRef.current === 'send_reminder') {
        if (selectedAccount) {
          sendReminder({
            reminder_type: 'bank_account_details',
            employee_ids: [Number(selectedAccount.id)],
          });
        }
      } else if (pendingRejectActionRef.current === 'update_manually') {
        toast.show({
          content: 'Bank account rejected successfully',
          color: 'positive',
        });
        if (selectedAccount) {
          history.push({
            pathname: routePaths.employeeDetailsV2.bankAccountDetails.edit,
            search: `?userId=${selectedAccount.id}`,
          });
        }
      }

      onClose();
      onRejectSuccess();
    },
    onError: (error: typeof AppError) => {
      toast.show({
        content: parseError(error),
        color: 'negative',
      });
    },
  });

  const handleDismiss = () => {
    if (!isRejecting) {
      onClose();
    }
  };

  const handleConfirm = () => {
    if (!selectedAccount) {
      return;
    }
    pendingRejectActionRef.current = selectedAction;
    rejectNameMismatch({ bankAccountId: selectedAccount.bank_account_id, reason });
  };

  return (
    <Modal isOpen={isOpen} onDismiss={handleDismiss} size="small" zIndex={MODAL_OVER_DRAWER_Z_INDEX}>
      <ModalHeader title="Reject Bank Account" />
      <ModalBody>
        <Box marginBottom="spacing.5">
          <Text color="surface.text.gray.subtle">
            Choose how you'd like to resolve the name mismatch for{' '}
            {selectedAccount?.name ?? 'this employee'}.
          </Text>
        </Box>
        <RadioGroup
          value={selectedAction}
          onChange={({ value }) => setSelectedAction(value as RejectAction)}>
          <Radio
            value="send_reminder"
            helpText={`Notify ${
              selectedAccount?.name ?? 'this employee'
            } to update their bank account details directly.`}>
            Reject & Send Reminder
          </Radio>
          <Radio
            value="update_manually"
            helpText="Go to their employee profile and correct the bank details on their behalf.">
            Reject & Update Manually
          </Radio>
        </RadioGroup>
        <Box marginTop="spacing.5">
          <TextArea
            label="Reason"
            necessityIndicator="optional"
            isRequired={false}
            placeholder="Add a reason for rejecting this bank account"
            value={reason}
            onChange={({ value }) => setReason(value ?? '')}
            numberOfLines={2}
          />
        </Box>
      </ModalBody>
      <ModalFooter>
        <Box display="flex" gap="spacing.3" justifyContent="flex-end" width="100%">
          <Button isDisabled={isRejecting} variant="tertiary" onClick={handleDismiss}>
            Cancel
          </Button>
          <Button
            isLoading={isRejecting}
            variant="primary"
            color="negative"
            onClick={handleConfirm}>
            Confirm
          </Button>
        </Box>
      </ModalFooter>
    </Modal>
  );
};
